import LocationOnIcon from '@mui/icons-material/LocationOn'
import SearchIcon from '@mui/icons-material/Search'
import { CircularProgress, Grid, TextField, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useMapsLibrary, useMap } from '@vis.gl/react-google-maps'
import parse from 'autosuggest-highlight/parse'
import throttle from 'lodash/throttle'
import { useTranslate } from 'ra-core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InputHelperText, useInput } from 'react-admin'
import { useFormContext } from 'react-hook-form'

import {
  ADDRESS_FIELD_COUNTRY,
  ADDRESS_FIELD_COUNTRY_CODE,
  ADDRESS_FIELD_LOCALITY,
  ADDRESS_FIELD_POSTAL_TOWN,
  ADDRESS_MAIN_FIELDS,
  PLACE_DETAILS_FIELD_ADDRESS_COMPONENTS,
  PLACE_DETAILS_FIELD_FORMATTED_ADDRESS,
  PLACE_DETAILS_FIELD_GEOMETRY,
  PLACE_DETAILS_FIELD_PLACE_ID,
  PLACE_DETAILS_FIELDS,
} from '../config/addresses'
import { useCommonStyles } from '../config/theme'
import { useSmallScreen } from '../utils/theme'

const placeToAddressObject = (place, transformAddressObject) => {
  const address = {
    [PLACE_DETAILS_FIELD_PLACE_ID]: place[PLACE_DETAILS_FIELD_PLACE_ID],
    [PLACE_DETAILS_FIELD_FORMATTED_ADDRESS]: place[PLACE_DETAILS_FIELD_FORMATTED_ADDRESS],
  }
  ADDRESS_MAIN_FIELDS.forEach((field) => {
    const fieldData = place[PLACE_DETAILS_FIELD_ADDRESS_COMPONENTS].find((c) => !!c?.types.includes(field))
    address[field] = fieldData?.long_name
    // We also store country code (alpha-2)
    if (field === ADDRESS_FIELD_COUNTRY) {
      address[ADDRESS_FIELD_COUNTRY_CODE] = fieldData?.short_name
    }
    // If no locality is found through Google API, we use a fallback on postal town
    if (field === ADDRESS_FIELD_LOCALITY && !Boolean(address[field])) {
      address[field] = place[PLACE_DETAILS_FIELD_ADDRESS_COMPONENTS].find(
        (c) => !!c?.types.includes(ADDRESS_FIELD_POSTAL_TOWN),
      )?.long_name
    }
  })
  return transformAddressObject(address)
}

const AddressInputBase = ({
  className,
  getOptionLabel,
  label,
  latLngSources,
  linkedSourcesOnClear,
  transformAddressObject = (object) => object,
  sx,
  ...props
}) => {
  const {
    field: { onChange, value, ...fieldProps },
    fieldState: { isTouched, error },
    formState: { isSubmitted },
    isRequired,
  } = useInput(props)

  const [highlightedOption, setHighlightedOption] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [autocompleteService, setAutocompleteService] = useState(null)
  const [placesService, setPlacesService] = useState(null)
  const { setValue } = useFormContext()
  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()
  const commonClasses = useCommonStyles()

  const sessionToken = useRef()

  const map = useMap() // get map instance via vis.gl
  const mapsLibrary = useMapsLibrary('places')
  const dummyDivRef = useRef(null)

  // Initialize services once library + map are available
  useEffect(() => {
    if (!mapsLibrary) return

    sessionToken.current = new mapsLibrary.AutocompleteSessionToken()

    // Create AutocompleteService
    setAutocompleteService(new mapsLibrary.AutocompleteService())

    if (map) {
      setPlacesService(new mapsLibrary.PlacesService(map))
    } else if (dummyDivRef.current) {
      setPlacesService(new mapsLibrary.PlacesService(dummyDivRef.current))
    }
  }, [mapsLibrary, map])

  const getPlacePredictions = useMemo(() => {
    if (!autocompleteService) return null
    return throttle((req, cb) => {
      autocompleteService.getPlacePredictions(req, cb)
    }, 200)
  }, [autocompleteService])

  const getPlaceDetails = useMemo(() => {
    if (!placesService) return null
    return (req, cb) => {
      placesService.getDetails(req, cb)
    }
  }, [placesService])

  const changeLatLngFields = useCallback(
    (location) => {
      if (!latLngSources) return
      if (location) {
        setValue(latLngSources[0], location.lat())
        setValue(latLngSources[1], location.lng())
      } else {
        setValue(latLngSources[0], null)
        setValue(latLngSources[1], null)
      }
    },
    [setValue, latLngSources],
  )

  const onChangeHandler = useCallback(
    (event, newValue) => {
      if (newValue) {
        getPlaceDetails(
          {
            placeId: newValue[PLACE_DETAILS_FIELD_PLACE_ID],
            fields: PLACE_DETAILS_FIELDS,
            sessionToken: sessionToken.current,
          },
          (place) => {
            onChange(placeToAddressObject(place, transformAddressObject))
            changeLatLngFields(place[PLACE_DETAILS_FIELD_GEOMETRY].location)
          },
        )
      } else {
        onChange(null)
        changeLatLngFields()
        if (linkedSourcesOnClear) {
          linkedSourcesOnClear.forEach((source) => setValue(source, null))
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      changeLatLngFields,
      getPlaceDetails,
      onChange,
      setValue,
      transformAddressObject,
      JSON.stringify(linkedSourcesOnClear), // eslint-disable-line react-hooks/exhaustive-deps
    ],
  )

  const onInputChangeHandler = useCallback(
    (event, newInputValue) => {
      setInputValue(newInputValue)
    },
    [setInputValue],
  )

  const onHighlightChangeHandler = useCallback(
    (event, option) => {
      setHighlightedOption(option)
    },
    [setHighlightedOption],
  )

  const renderInput = (params) => (
    <TextField
      {...params}
      label={label}
      required={isRequired}
      error={Boolean(error) && (isTouched || isSubmitted)}
      helperText={<InputHelperText touched={isTouched || isSubmitted} error={error?.message} />}
      sx={sx ? sx : isSmallScreen ? commonClasses.commonInput : commonClasses.doubleInput}
      InputProps={{
        ...params.InputProps,
        startAdornment: <SearchIcon sx={{ m: '2px 2px 0 5px', width: '19px', height: '19px', color: 'contrast' }} />,
        endAdornment: (
          <>
            {isLoading ? <CircularProgress size={20} style={{ marginLeft: 10 }} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
      inputProps={{
        ...params.inputProps,
        onKeyDown: (e) => {
          // Prevent submitting the whole form on pressing enter with
          // an empty input / half-filled input
          if (e.key === 'Enter' && !highlightedOption) {
            e.preventDefault()
            e.stopPropagation()
          }
        },
      }}
    />
  )

  const renderOption = (props, option) => {
    const matches = option.structured_formatting?.main_text_matched_substrings
    const parts = parse(
      option.structured_formatting?.main_text,
      matches?.map((m) => [m.offset, m.offset + m.length]) ?? [],
    )
    return (
      <li {...props}>
        <Grid container alignItems="center">
          <Grid>
            <LocationOnIcon />
          </Grid>
          <Grid size="grow">
            {parts.map((part, index) => (
              <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                {part.text}
              </span>
            ))}
            <Typography variant="body2" color="textSecondary">
              {option.structured_formatting?.secondary_text}
            </Typography>
          </Grid>
        </Grid>
      </li>
    )
  }

  useEffect(() => {
    let active = true
    if (!autocompleteService || !placesService || !sessionToken.current) {
      return
    }

    if (inputValue === '') {
      setOptions(value ? [value] : [])
      return
    }

    setIsLoading(true)
    getPlacePredictions(
      {
        input: inputValue,
        sessionToken: sessionToken.current,
        types: ['geocode'],
      },
      (results) => {
        if (!active) return
        let newOptions = []
        if (value) {
          newOptions = [value]
        }
        if (results) {
          newOptions = [...newOptions, ...results]
        }
        setIsLoading(false)
        setOptions(newOptions)
      },
    )
    return () => (active = false)
  }, [value, inputValue, getPlacePredictions])

  return (
    <>
      <div ref={dummyDivRef} style={{ display: 'none' }} />
      <Autocomplete
        {...fieldProps}
        size="small"
        noOptionsText={translate('mymove.enterAnAddress')}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          option[PLACE_DETAILS_FIELD_PLACE_ID] === value[PLACE_DETAILS_FIELD_PLACE_ID]
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value || null}
        onChange={onChangeHandler}
        onInputChange={onInputChangeHandler}
        onHighlightChange={onHighlightChangeHandler}
        renderInput={renderInput}
        renderOption={renderOption}
      />
    </>
  )
}

export default AddressInputBase
